// tabNavigation.ts — 하단 탭 소속 **단일 사전 + 단일 판정** (design doc 2026-09-07-review-tab-stage1 §0-2)
//
// 왜 모듈로 뺐나: 종전엔 BottomNav가 그룹 배열 3개를 탭별 OR(startsWith)로 계산해 /sim/reflect가
//   모의투자(/sim)와 복기 둘 다에 걸렸고, useBackTo는 자기 PATH_GROUPS를 따로 들고 있어 사전이 두 벌이었다.
//   BottomNav(강조)·useBackTo(직접 진입 fallback)·SwipeBack(루트에서 스와이프 금지)이 이 모듈만 쓴다.
//
// 판정 규칙 (Astra 프로브 49케이스 통과 규칙):
//   ① '/'는 정확 일치만 홈.
//   ② 경계 매칭 — pathname === base 또는 pathname.startsWith(base + '/'). ('/similar'가 '/sim'에 걸리지 않는다.)
//   ③ 여러 base가 맞으면 **가장 긴 base가 이긴다** ('/sim/reflect' > '/sim'). 그룹 배열 순서에 의존하지 않는다.
//   ④ 어디에도 없으면 null — 강조 없음. back fallback만 '/'.

export type TabRoot = '/' | '/games' | '/portfolio-analysis' | '/community' | '/my';

// 하단 탭 5개 — 순서 확정 [홈·모의투자·복기·커뮤니티·MY] (효신님 2026-09-07). 복기가 중앙.
export const TABS: ReadonlyArray<{ to: TabRoot; label: string; icon: string }> = [
  { to: '/', label: '홈', icon: 'home' },
  { to: '/games', label: '모의투자', icon: 'chart' },
  { to: '/portfolio-analysis', label: '복기', icon: 'clipboard' },
  { to: '/community', label: '커뮤니티', icon: 'chat' },
  { to: '/my', label: 'MY', icon: 'user' },
];

// 모의투자 — 매매·발굴·거래도구·6엔진. 복기 계열 4개는 REVIEW_PATHS로 이동(2026-09-07).
export const SIM_PATHS: readonly string[] = [
  '/games', '/sim', '/discover', '/stock', '/compare', '/alerts', '/leaderboard', '/scrap',
  '/macro-event', '/buy-or-not', '/challenge-board', '/turnaround-radar', '/heatmap', '/indicator-studio',
];

// 복기 — 허브·감정서·전시실(+/brag)·토론 기록·코치 기록·자동 복기 설정.
//   /sim/reflect는 '/sim'보다 긴 base라 규칙 ③으로 여기에 온다. /review-auto는 종전 어느 그룹에도 없던 누락(A1).
export const REVIEW_PATHS: readonly string[] = [
  '/portfolio-analysis', '/sim/reflect', '/failmuseum', '/debate-review', '/review-history', '/review-auto',
];

// 옛 금융 탭 하위 — 화면은 그대로, 소속만 MY(자산·성향 도구). /future-price는 실험실로.
export const FINANCE_PATHS: readonly string[] = [
  '/finance', '/budget', '/asset', '/mbti', '/signature', '/goals', '/about', '/risk', '/csv-import',
];

// MY 실험실 — 미래가격 계산기·자산 시뮬레이션(3단계). /play는 종전 어느 그룹에도 없던 누락.
export const LAB_PATHS: readonly string[] = ['/future-price', '/play'];

export const MY_PATHS: readonly string[] = [
  '/my', '/account-link', '/collection', '/gacha', '/cards', '/hall', '/enhance', '/messages',
  ...FINANCE_PATHS, ...LAB_PATHS,
];

export const PATH_GROUPS: ReadonlyArray<{ root: TabRoot; paths: readonly string[] }> = [
  { root: '/games', paths: SIM_PATHS },
  { root: '/portfolio-analysis', paths: REVIEW_PATHS },
  { root: '/community', paths: ['/community'] },
  { root: '/my', paths: MY_PATHS },
];

// 탭 루트 5개 — 돌아갈 상위가 없어 SwipeBack 비활성. (종전 SwipeBack 안의 하드코딩 Set을 여기로.)
export const TAB_ROOTS: ReadonlySet<string> = new Set(TABS.map((t) => t.to));

// 뒤 '/' 제거 — '/games/'도 '/games'와 같은 루트. 쿼리·해시는 호출측(pathname)에 이미 없다.
function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '');
  return pathname;
}

function matches(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + '/');
}

/** 경로가 속한 탭 루트. 없으면 null(강조 없음). */
export function resolveTabRoot(pathname: string): TabRoot | null {
  const p = normalize(pathname);
  if (p === '/') return '/';
  let best: { root: TabRoot; len: number } | null = null;
  for (const group of PATH_GROUPS) {
    for (const base of group.paths) {
      if (matches(p, base) && (!best || base.length > best.len)) best = { root: group.root, len: base.length };
    }
  }
  return best?.root ?? null;
}

/** 히스토리가 없을 때 돌아갈 곳 — 소속 탭 루트, 소속 없으면 홈. */
export function resolveBackTarget(pathname: string): TabRoot {
  return resolveTabRoot(pathname) ?? '/';
}

/** 탭 루트인가(스와이프 뒤로가기 비활성 판정). */
export function isTabRoot(pathname: string): boolean {
  return TAB_ROOTS.has(normalize(pathname));
}
